import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import {
  linkedFilesStepValidate,
  navigateToChangeStrategy,
  navigateToStepMileStones,
  onReset,
} from './utils/index.js';
import { retrieveProject, selectProject } from '../../../modules/projects.js';
import { mapFinalFormErrors } from '../../../lib/utils.js';
import {
  deleteChangeStrategyDraft,
  retrieveChangeStrategy,
  selectChangeStrategy,
  selectDraftChangeStrategy,
  updateChangeStrategy,
  updateChangeStrategyDraft,
} from '../../../modules/changeStrategy.js';
import StepLinkedFilesFrom from '../../../components/projects/ChangeStrategy/ChnageStrategyForms/StepLinkedFilesForm.jsx';
import ChangeStrategyFormHeader from '../../../components/headers/PageHeaders/Project/ChangeStrategy/ChangeStrategyFormHeader.jsx';
import MultistepCombinedHeader from '../../../components/headers/FormHeaders/MultistepCombinedHeader.jsx';

const mapErrors = mapFinalFormErrors('Failed to save change strategy');

const mapStateToProps = (state, ownProps) => ({
  editing: !!state.changeStrategy.changeStrategy,
  projectId: ownProps.projectId,
  project: selectProject(ownProps.projectId)(state),
  changeStrategy: selectChangeStrategy(state),
  changeStrategyDraft: selectDraftChangeStrategy(state),
});

const mapDispatchToProps = {
  deleteChangeStrategyDraft,
  retrieveChangeStrategy,
  retrieveProject,
  updateChangeStrategyDraft,
  updateChangeStrategy,
};

function StepLinkedFilesFormContainer({
  slug,
  projectId,
  editing,
  changeStrategyDraft,
  updateChangeStrategy,
  deleteChangeStrategyDraft,
  project,
}) {
  const [loading, setLoading] = useState(false);

  const goBack = () => navigateToStepMileStones(editing, projectId, slug);
  const goStart = () => navigateToChangeStrategy(projectId, slug);

  const onCancel = () => {
    deleteChangeStrategyDraft(projectId).then(() => {
      navigateToChangeStrategy(projectId, slug);
    });
  };

  const linkedFiles = changeStrategyDraft?.linked_files.length
    ? changeStrategyDraft.linked_files.map((file, index) => ({
        [`id${index}`]: null,
        [`name${index}`]: file.name,
        [`url${index}`]: file.url,
        [`groupRoles${index}`]: file.group_roles,
      }))
    : [];

  const [columns, setColumns] = useState(linkedFiles);

  const onSubmit = async (values) => {
    const { finish } = values;
    setLoading(true);

    const isValidLinkedFiles = await linkedFilesStepValidate(columns);

    if (Object.keys(isValidLinkedFiles).length === 0) {
      const linkedFiles = (
        columns.length && columns[0].name0 && columns[0].url0 ? columns : []
      )
        .map((likedFile, index) => {
          const groupRoles = [];
          if (values[`viewer${index}`]) {
            groupRoles.push('viewer');
          }
          if (values[`change_advocate${index}`]) {
            groupRoles.push('change_advocate');
          }
          return {
            id: likedFile[`id${index}`],
            name: likedFile[`name${index}`],
            url: likedFile[`url${index}`],
            group_roles: groupRoles,
          };
        })
        .filter((milestone) => milestone.name && milestone?.url);
      if (finish) {
        updateChangeStrategy(
          projectId,
          { change_strategy: { linked_files: linkedFiles } },
          true,
          true
        ).then(() => {
          goStart();
        });
      }
    } else {
      setLoading(false);
    }
  };
  const onCreateColumn = () =>
    setColumns([
      ...columns,
      {
        [`id${columns.length}`]: null,
        [`name${columns.length}`]: '',
        [`url${columns.length}`]: '',
        [`groupRoles${columns.length}`]: [],
      },
    ]);

  const deleteAndAdjustIndex = (array, indexToDelete) => {
    if (indexToDelete < 0 || indexToDelete >= array.length) {
      throw new Error('Invalid index');
    }

    array.splice(indexToDelete, 1); // Remove the item at the specified index

    return array.map((item, index) => {
      const newItem = {};

      for (const key in item) {
        const match = key.match(/^(.*?)(\d+)$/);

        if (match && match[2] == index + 1) {
          const newKey = match[1] + index;
          newItem[newKey] = item[key];
        } else {
          newItem[key] = item[key];
        }
      }

      return newItem;
    });
  };

  const onDeleteColumn = (deleteIndex) => {
    const deleteLinkedFile = columns.find(
      (column, index) => deleteIndex === index
    );
    const name = deleteLinkedFile[`name${deleteIndex}`];
    if (!window.confirm('Are you sure you want to delete this linked file?'))
      return;

    const filteredMiles = deleteAndAdjustIndex(columns, deleteIndex);
    setColumns(filteredMiles);
  };

  const onColumnNameChange = (value, itemIndex) => {
    setColumns(
      columns.map((item, index) =>
        itemIndex === index ? { ...item, [`name${itemIndex}`]: value } : item
      )
    );
  };

  const onColumnUrlChange = (value, itemIndex) => {
    setColumns(
      columns.map((item, index) =>
        itemIndex === index ? { ...item, [`url${itemIndex}`]: value } : item
      )
    );
  };

  const validate = () => linkedFilesStepValidate(columns);

  const initial = {};

  useEffect(() => {
    !changeStrategyDraft && onReset(editing, projectId, slug);
  }, []);

  return (
    <React.Fragment>
      <MultistepCombinedHeader
        editing={editing}
        subject="Change Strategy"
        typeStep="Linked files"
      >
        {project && (
          <ChangeStrategyFormHeader
            slug={slug}
            project={project}
            editing={editing}
          />
        )}
      </MultistepCombinedHeader>
      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              <Row>
                <Col sm={10} smOffset={1}>
                  <Form
                    component={StepLinkedFilesFrom}
                    validate={validate}
                    onCreateColumn={onCreateColumn}
                    onColumnNameChange={onColumnNameChange}
                    onColumnUrlChange={onColumnUrlChange}
                    onDeleteColumn={onDeleteColumn}
                    initialValues={initial}
                    data={columns}
                    projectId={projectId}
                    onSubmit={onSubmit}
                    onCancel={onCancel}
                    onBack={goBack}
                  />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Grid>
    </React.Fragment>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StepLinkedFilesFormContainer);
